// src/gestion-logistics/components/ShipmentFilters.tsx
import React from "react";
import { Row, Col, Input, Select, DatePicker, Button, Space } from "antd";
import { SearchOutlined, ReloadOutlined } from "@ant-design/icons";
import { ShipmentStatus, TransportMode, ShipmentFilters as Filters } from "../types/shipment.types";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { Option } = Select;

interface ShipmentFiltersProps {
  filters: Filters;
  onFilterChange: (filters: Filters) => void;
  onReset: () => void;
  loading?: boolean;
}

/**
 * Composant de filtres pour la liste des dossiers
 */
const ShipmentFilters: React.FC<ShipmentFiltersProps> = ({
  filters,
  onFilterChange,
  onReset,
  loading = false,
}) => {
  const handleSearchChange = (value: string) => {
    onFilterChange({ ...filters, search: value || undefined });
  };

  const handleStatusChange = (value: ShipmentStatus | undefined) => {
    onFilterChange({ ...filters, status: value });
  };

  const handleTransportModeChange = (value: TransportMode | undefined) => {
    onFilterChange({ ...filters, transport_mode: value });
  };

  const handleDateRangeChange = (dates: any) => {
    if (dates && dates.length === 2) {
      onFilterChange({
        ...filters,
        date_from: dates[0].format("YYYY-MM-DD"),
        date_to: dates[1].format("YYYY-MM-DD"),
      });
    } else {
      onFilterChange({
        ...filters,
        date_from: undefined,
        date_to: undefined,
      });
    }
  };

  const dateRange =
    filters.date_from && filters.date_to
      ? [dayjs(filters.date_from), dayjs(filters.date_to)]
      : undefined;

  return (
    <div
      style={{
        background: "#fafafa",
        padding: "16px",
        borderRadius: "8px",
        marginBottom: "16px",
      }}
    >
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Input
            placeholder="Rechercher (N° dossier, client...)"
            prefix={<SearchOutlined />}
            value={filters.search}
            onChange={(e) => handleSearchChange(e.target.value)}
            allowClear
            disabled={loading}
          />
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Select
            placeholder="Statut"
            value={filters.status}
            onChange={handleStatusChange}
            allowClear
            style={{ width: "100%" }}
            disabled={loading}
          >
            <Option value={ShipmentStatus.DRAFT}>Brouillon</Option>
            <Option value={ShipmentStatus.CONFIRMED}>Confirmé</Option>
            <Option value={ShipmentStatus.IN_TRANSIT}>En Transit</Option>
            <Option value={ShipmentStatus.DELIVERED}>Livré</Option>
            <Option value={ShipmentStatus.COMPLETED}>Terminé</Option>
            <Option value={ShipmentStatus.CANCELLED}>Annulé</Option>
          </Select>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Select
            placeholder="Mode de transport"
            value={filters.transport_mode}
            onChange={handleTransportModeChange}
            allowClear
            style={{ width: "100%" }}
            disabled={loading}
          >
            <Option value={TransportMode.SEA}>Maritime</Option>
            <Option value={TransportMode.AIR}>Aérien</Option>
            <Option value={TransportMode.ROAD}>Routier</Option>
            <Option value={TransportMode.RAIL}>Ferroviaire</Option>
            <Option value={TransportMode.MULTIMODAL}>Multimodal</Option>
          </Select>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Space style={{ width: "100%" }}>
            <RangePicker
              value={dateRange as any}
              onChange={handleDateRangeChange}
              format="DD/MM/YYYY"
              placeholder={["Date début", "Date fin"]}
              style={{ width: "100%" }}
              disabled={loading}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={onReset}
              disabled={loading}
              title="Réinitialiser les filtres"
            />
          </Space>
        </Col>
      </Row>
    </div>
  );
};

export default ShipmentFilters;
